import type { PrStatus } from '../../shared/rules/statuses';
import { useI18n, type MessageKey } from '../i18n';
import { StatusPill } from './StatusPill';

// "What do the statuses mean?" (D102): the approved status glossary, folded under the list it
// explains. Requesters get their own words (no invoice or payment, D100); staff get every status.

const REQUESTER: readonly PrStatus[] = [
  'Draft',
  'ProcurementReview',
  'WithDeptHead',
  'Returned',
  'Sourcing',
  'PettyCash',
  'PODraft',
  'PendingCFO',
  'POIssued',
  'PartlyReceived',
  'Received',
  'OnHold',
  'Rejected',
  'Cancelled',
];

const STAFF: readonly PrStatus[] = [
  'Draft',
  'ProcurementReview',
  'WithDeptHead',
  'Returned',
  'Sourcing',
  'PettyCash',
  'PODraft',
  'PendingCFO',
  'PendingVertical',
  'POSentPendingCFO',
  'POIssued',
  'PartlyReceived',
  'Received',
  'Invoiced',
  'VoucherWithFinance',
  'Paid',
  'Closed',
  'OnHold',
  'Rejected',
  'Cancelled',
];

const OWN_WORDS: ReadonlySet<PrStatus> = new Set<PrStatus>(['Received']);

export function StatusGuide({ staff }: { staff: boolean }) {
  const { t } = useI18n();
  // Delivered is the end for the requester (D100), so it reads differently for them.
  const key = (s: PrStatus, part: 'meaning' | 'next') =>
    (!staff && OWN_WORDS.has(s) ? `gl.${s}.${part}.own` : `gl.${s}.${part}`) as MessageKey;
  return (
    <details class="card status-guide">
      <summary>{t('gl.title')}</summary>
      {/* Scrolls sideways on a phone, so it takes keyboard focus (WCAG 2.1.1). */}
      <div
        class="table-wrap"
        // A scrollable region must be reachable by keyboard (axe scrollable-region-focusable).
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        role="region"
        aria-label={t('gl.title')}
      >
        <table class="table">
          <thead>
            <tr>
              <th scope="col">{t('gl.col.status')}</th>
              <th scope="col">{t('gl.col.meaning')}</th>
              <th scope="col">{t('gl.col.next')}</th>
            </tr>
          </thead>
          <tbody>
            {(staff ? STAFF : REQUESTER).map((s) => (
              <tr key={s}>
                <td>
                  <StatusPill status={s} own={!staff} />
                </td>
                <td>{t(key(s, 'meaning'))}</td>
                <td>{t(key(s, 'next'))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
